import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useI18n } from "@/contexts/I18nContext";
import * as Icons from "lucide-react";
import { ArrowRight, Briefcase, Building2, Users, ShieldCheck, Search } from "lucide-react";
import api from "@/lib/api";
import ReadAloud from "@/components/ReadAloud";
import useSEO from "@/lib/useSEO";

export default function Landing() {
  const { lang, t } = useI18n();
  const [cats, setCats] = useState([]);
  const [query, setQuery] = useState("");
  const nav = useNavigate();

  useSEO({
    title: "Sarthi Associates — Frontline Workforce for Power, Refinery & EPC Projects",
    description: "Find or hire welders, fitters, riggers, electricians, technicians, plumbers. 1,500+ verified workers · 20+ EPC contractor partners across India. Voice-enabled & multilingual (Hindi/English/Marathi/Tamil).",
    path: "/",
    lang: lang === "en" ? "en-IN" : `${lang}-IN`,
    jsonLd: {
      "@context": "https://schema.org",
      "@graph": [
        {
          "@type": "EmploymentAgency",
          "@id": "https://sarthiassociates.com/#agency",
          "name": "Sarthi Associates",
          "url": "https://sarthiassociates.com",
          "logo": "https://sarthiassociates.com/sarthi-logo.png",
          "description": "India's frontline-workforce recruitment platform for EPC contractors, thermal power plants, alumina refineries and heavy industrial employers.",
          "address": { "@type": "PostalAddress", "addressLocality": "Noida", "addressRegion": "UP", "addressCountry": "IN" },
          "areaServed": { "@type": "Country", "name": "India" },
          "telephone": "+91-91318-47732",
          "email": "contact@sarthiassociates.com",
          "priceRange": "Free for workers · Premium plans for recruiters",
          "founder": { "@type": "Person", "name": "Sanjay Singh Yadav" },
          "knowsLanguage": ["en-IN", "hi-IN", "mr-IN", "ta-IN"],
          "makesOffer": [
            { "@type": "Offer", "name": "Worker registration", "price": "0", "priceCurrency": "INR" },
            { "@type": "Offer", "name": "Premium Plans for Business Partners", "url": "https://sarthiassociates.com/plans" },
            { "@type": "Offer", "name": "Engineering Consultancy bookings", "url": "https://sarthiassociates.com/consultancy" }
          ]
        },
        {
          "@type": "ItemList",
          "name": "Frontline trades on Sarthi Associates",
          "itemListElement": [
            { "@type": "ListItem", "position": 1, "name": "Welder", "url": "https://sarthiassociates.com/jobs?category=welder" },
            { "@type": "ListItem", "position": 2, "name": "Fitter", "url": "https://sarthiassociates.com/jobs?category=fitter" },
            { "@type": "ListItem", "position": 3, "name": "Electrician", "url": "https://sarthiassociates.com/jobs?category=electrician" },
            { "@type": "ListItem", "position": 4, "name": "Plumber", "url": "https://sarthiassociates.com/jobs?category=plumber" },
            { "@type": "ListItem", "position": 5, "name": "Rigger", "url": "https://sarthiassociates.com/jobs?category=rigger" },
            { "@type": "ListItem", "position": 6, "name": "Technician", "url": "https://sarthiassociates.com/jobs?category=technician" },
            { "@type": "ListItem", "position": 7, "name": "Helper", "url": "https://sarthiassociates.com/jobs?category=helper" },
            { "@type": "ListItem", "position": 8, "name": "Mason", "url": "https://sarthiassociates.com/jobs?category=mason" }
          ]
        }
      ]
    },
  });

  useEffect(() => {
    api.get("/categories").then((r) => setCats(r.data)).catch(() => {});
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const goSearch = (e) => {
    e?.preventDefault?.();
    const q = query.trim();
    nav(q ? `/jobs?q=${encodeURIComponent(q)}` : "/jobs");
  };

  const nameFor = (c) => c[`name_${lang}`] || c.name_en;

  return (
    <div>
      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="mx-auto grid max-w-7xl grid-cols-1 gap-10 px-4 pb-12 pt-10 sm:px-6 lg:grid-cols-2 lg:gap-16 lg:pt-16">
          <div>
            <div className="mb-4 inline-flex items-center gap-2 rounded-full bg-sarthi-accent px-4 py-2 text-sm font-bold uppercase tracking-wider text-sarthi-primary">
              <ShieldCheck className="h-4 w-4" /> {t("tagline")}
            </div>
            <h1 className="font-display text-4xl font-black leading-[1.05] text-sarthi-text sm:text-5xl lg:text-6xl">
              {t("hero_headline")}
            </h1>
            <p className="mt-5 max-w-xl text-lg font-medium text-sarthi-muted sm:text-xl">
              {t("hero_sub")}
            </p>
            <div className="mt-3">
              <ReadAloud text={`${t("hero_headline")}. ${t("hero_sub")}`} />
            </div>

            {/* Integrated search bar */}
            <form onSubmit={goSearch} className="mt-6 flex w-full max-w-xl items-center gap-2 rounded-2xl border-2 border-sarthi-border bg-white p-2 shadow-[0_4px_0_0_rgba(0,0,0,0.05)] focus-within:border-sarthi-primary">
              <div className="flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-xl bg-sarthi-accent text-sarthi-primary">
                <Search className="h-5 w-5" strokeWidth={2.5} />
              </div>
              <input
                type="text"
                data-testid="hero-search-input"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder={lang === "hi" ? "काम खोजें — वेल्डर, फिटर, नोएडा…" : lang === "mr" ? "काम शोधा — वेल्डर, फिटर, नोएडा…" : lang === "ta" ? "வேலை தேட — வெல்டர், பிட்டர்…" : "Search jobs — welder, fitter, Noida…"}
                className="min-w-0 flex-1 bg-transparent px-2 text-base font-semibold text-sarthi-text placeholder:text-sarthi-muted focus:outline-none"
              />
              <button
                type="submit"
                data-testid="hero-search-btn"
                className="sarthi-btn-primary inline-flex flex-shrink-0 items-center gap-1.5 px-4 py-2.5 text-sm text-white sm:text-base"
              >
                <Search className="h-4 w-4" />
                <span className="hidden sm:inline">{t("browse_jobs")}</span>
                <span className="sm:hidden">Go</span>
              </button>
            </form>

            <div className="mt-8 flex flex-col gap-4 sm:flex-row">
              <Link
                to="/register?role=worker"
                data-testid="cta-find-work"
                className="sarthi-btn-primary inline-flex items-center justify-center gap-3 px-6 py-4 text-lg text-white sm:text-xl"
              >
                <Briefcase className="h-6 w-6" /> {t("find_work")} <ArrowRight className="h-5 w-5" />
              </Link>
              <Link
                to="/register?role=contractor"
                data-testid="cta-post-work"
                className="sarthi-btn-secondary inline-flex items-center justify-center gap-3 px-6 py-4 text-lg text-white sm:text-xl"
              >
                <Building2 className="h-6 w-6" /> {t("post_work")} <ArrowRight className="h-5 w-5" />
              </Link>
            </div>
            <div className="mt-6 flex items-center gap-4 text-sm font-semibold text-sarthi-muted">
              <div className="flex items-center gap-2"><Users className="h-4 w-4" /> 1,500+ workers</div>
              <div className="hidden sm:block">•</div>
              <div className="flex items-center gap-2"><Building2 className="h-4 w-4" /> 20+ contractors</div>
            </div>
          </div>
          <div className="relative">
            <div className="sarthi-card overflow-hidden">
              <img
                src="https://images.pexels.com/photos/8293670/pexels-photo-8293670.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
                alt="Frontline worker"
                className="h-[360px] w-full object-cover sm:h-[460px]"
                data-testid="hero-image"
              />
            </div>
            <div className="absolute -bottom-4 -left-4 hidden rounded-2xl border-2 border-sarthi-border bg-white p-4 shadow-[0_6px_0_0_rgba(0,0,0,0.08)] sm:block">
              <div className="text-xs font-bold uppercase text-sarthi-muted">{t("demo_otp_hint")}</div>
              <div className="font-display text-lg font-extrabold text-sarthi-primary">Mobile OTP → 123456</div>
            </div>
          </div>
        </div>
      </section>

      {/* Categories */}
      <section className="border-t-2 border-sarthi-border bg-white">
        <div className="mx-auto max-w-7xl px-4 py-14 sm:px-6">
          <div className="mb-8 flex items-end justify-between gap-4">
            <div>
              <h2 className="font-display text-3xl font-extrabold text-sarthi-text sm:text-4xl">{t("categories")}</h2>
              <p className="mt-1 text-base font-medium text-sarthi-muted">{t("select_skills")}</p>
            </div>
            <Link to="/jobs" data-testid="see-all-jobs" className="sarthi-btn-outline hidden px-4 py-3 sm:inline-flex">
              {t("browse_jobs")} →
            </Link>
          </div>
          <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 md:grid-cols-4">
            {cats.map((c) => {
              const Icon = Icons[c.icon] || Icons.Wrench;
              const jobsCount = c.active_jobs || 0;
              return (
                <Link
                  key={c.key}
                  to={`/jobs?category=${c.key}`}
                  data-testid={`category-card-${c.key}`}
                  className={`sarthi-card sarthi-card-press group relative flex flex-col items-center gap-3 overflow-hidden p-5 text-center transition-all duration-200 hover:-translate-y-1 hover:shadow-[0_10px_0_-4px_rgba(15,23,42,0.15)] cat-${c.color}`}
                >
                  {c.icon_url ? (
                    <img src={c.icon_url} alt={nameFor(c)} className="h-14 w-14 rounded-xl bg-white/70 object-cover p-1 transition-transform duration-200 group-hover:scale-110" />
                  ) : (
                    <div className="flex h-14 w-14 items-center justify-center rounded-xl bg-white/70 transition-transform duration-200 group-hover:scale-110">
                      <Icon className="h-8 w-8" strokeWidth={2.5} />
                    </div>
                  )}
                  <div className="font-display text-lg font-extrabold leading-tight">{nameFor(c)}</div>
                  {jobsCount > 0 && (
                    <span className="absolute right-2 top-2 rounded-full bg-slate-900 px-2 py-0.5 text-[10px] font-black uppercase tracking-wider text-white shadow" data-testid={`cat-count-${c.key}`}>
                      {jobsCount} live
                    </span>
                  )}
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* Leadership & Vision (integrated preview from /about) */}
      <section className="border-t-2 border-sarthi-border bg-white">
        <div className="mx-auto grid max-w-7xl grid-cols-1 gap-8 px-4 py-14 sm:px-6 lg:grid-cols-5 lg:gap-12">
          <div className="lg:col-span-2">
            <div className="text-sm font-bold uppercase tracking-wider text-sarthi-primary">{t("our_leadership")}</div>
            <div className="sarthi-card mt-4 overflow-hidden" data-testid="landing-md-card">
              <img
                src="https://customer-assets-v7afamib.emergentagent.net/job_sarthi-jobs/artifacts/1jdws7ge_SANJAY%20pics.jpeg"
                alt="Mr. Sanjay Singh Yadav"
                className="h-72 w-full object-cover object-top"
                onError={(e) => { e.currentTarget.src = "https://images.pexels.com/photos/2182970/pexels-photo-2182970.jpeg?auto=compress&cs=tinysrgb&w=800"; }}
              />
              <div className="p-5">
                <div className="font-display text-xl font-extrabold text-sarthi-text">Mr. Sanjay Singh Yadav</div>
                <div className="text-xs font-bold uppercase tracking-wide text-sarthi-primary">Managing Director &amp; Founder</div>
                <div className="mt-2 text-sm font-medium text-sarthi-muted">{t("md_short_bio")}</div>
              </div>
            </div>
          </div>

          <div className="lg:col-span-3">
            <div className="text-sm font-bold uppercase tracking-wider text-sarthi-primary">{t("our_vision")}</div>
            <blockquote className="mt-4 rounded-2xl bg-sarthi-accent p-6 text-lg font-medium italic leading-relaxed text-sarthi-text sm:text-xl" data-testid="landing-vision">
              &ldquo;{t("vision_short")}&rdquo;
            </blockquote>
            <div className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div className="sarthi-card p-5">
                <div className="mb-2 flex h-10 w-10 items-center justify-center rounded-xl bg-sarthi-primary text-white">
                  <Icons.Compass className="h-5 w-5" strokeWidth={2.5} />
                </div>
                <div className="font-display text-lg font-extrabold text-sarthi-text">Trusted platform</div>
                <div className="mt-1 text-sm font-medium text-sarthi-muted">Verified workers &amp; contractors — every profile earns its place.</div>
              </div>
              <div className="sarthi-card p-5">
                <div className="mb-2 flex h-10 w-10 items-center justify-center rounded-xl bg-sarthi-secondary text-white">
                  <Icons.Target className="h-5 w-5" strokeWidth={2.5} />
                </div>
                <div className="font-display text-lg font-extrabold text-sarthi-text">Skill first</div>
                <div className="mt-1 text-sm font-medium text-sarthi-muted">Match by real skill, not paperwork. Your hands do the talking.</div>
              </div>
            </div>
            <div className="mt-6">
              <Link to="/about" data-testid="landing-about-link" className="sarthi-btn-outline inline-flex items-center gap-2 px-5 py-3 text-base">
                {t("read_full_story")}
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* How it works */}
      <section className="border-t-2 border-sarthi-border">
        <div className="mx-auto grid max-w-7xl grid-cols-1 gap-6 px-4 py-14 sm:px-6 md:grid-cols-3">
          {[
            { key: "signup", icon: "UserPlus", title: {en: "Sign up in 30 sec", hi: "30 सेकंड में साइनअप", mr: "30 सेकंदात नोंदणी", ta: "30 வினாடிகளில் பதிவு"}, body: {en: "Use mobile OTP or email.", hi: "मोबाइल OTP या ईमेल से", mr: "मोबाइल OTP किंवा ईमेल", ta: "மொபைல் OTP அல்லது மின்னஞ்சல்"} },
            { key: "find", icon: "Search", title: {en: "Find nearby work", hi: "पास का काम खोजें", mr: "जवळचे काम शोधा", ta: "அருகிலுள்ள வேலை"}, body: {en: "Filter by skill and city.", hi: "हुनर और शहर से", mr: "कौशल्य आणि शहरानुसार", ta: "திறமை மற்றும் நகரம்"} },
            { key: "hired", icon: "HandCoins", title: {en: "Get hired & paid", hi: "काम पाइए और पैसा", mr: "काम मिळवा आणि पगार", ta: "வேலை பெறு & சம்பாதிக்கவும்"}, body: {en: "Direct contact with contractors.", hi: "ठेकेदार से सीधा संपर्क", mr: "थेट कंत्राटदाराशी संपर्क", ta: "நேரடி தொடர்பு"} },
          ].map((s) => {
            const Icon = Icons[s.icon] || Icons.Star;
            return (
              <div key={s.key} className="sarthi-card p-6">
                <div className="mb-3 flex h-12 w-12 items-center justify-center rounded-xl bg-sarthi-primary text-white shadow-[0_3px_0_0_#B84A25]">
                  <Icon className="h-6 w-6" strokeWidth={2.5} />
                </div>
                <div className="font-display text-xl font-extrabold text-sarthi-text">{s.title[lang] || s.title.en}</div>
                <div className="mt-1 text-base font-medium text-sarthi-muted">{s.body[lang] || s.body.en}</div>
              </div>
            );
          })}
        </div>
      </section>

      <footer className="border-t-2 border-sarthi-border bg-white py-8">
        <div className="mx-auto flex max-w-7xl flex-col items-center gap-3 px-4 text-center text-sm font-semibold text-sarthi-muted sm:flex-row sm:justify-between sm:px-6">
          <div>© 2026 {t("brand")} · Empowering India&apos;s frontline workforce</div>
          <Link to="/about" data-testid="footer-about-link" className="font-bold text-sarthi-primary hover:underline">
            {t("about")} →
          </Link>
        </div>
      </footer>
    </div>
  );
}
